import {
  cardHeightForSingleRowGroup, VEHICLE_LIST_HEADER_HEIGHT,
} from 'src/components/dispatch/vehicleCardStyles';

/**
 * 左カラムと中央ボードのヘッダ高さ。**両方で同じ値を使う。**
 *
 * 左は既存の車両リスト（`ConvenienceDispatchVehicleList`）のヘッダ 2 段、中央は日付ヘッダで
 * 中身が違うが、高さが揃っていないとその差がそのまま「車両行とレーンの縦ずれ」になる
 * （実測 62px ずれていた）。左の高さは既存リストが決めるので、こちらがそれを引く。
 * カード型配車も同じ定数を左右で共有して同じ問題を防いでいる。
 */
export const LONG_HAUL_HEADER_HEIGHT = VEHICLE_LIST_HEADER_HEIGHT;

/**
 * レーン 1 本の高さ。左カラムの行と中央のレーンで共有する。
 *
 * サンプル（route-planner `pages/MatchingPresenter/index.tsx:117` の
 * `MULTIDAY_ROW_HEIGHT`）と同じ 116px。サンプルは単日の 72px に対して長距離だけ
 * この値を左右へ渡していて、運行バーが 3 行（荷主 / 積地 → 降地 / 都道府県区間）を
 * 収められるのはこの高さがあるため。
 *
 * Why not 車両カードの外形（98.5px）をそのまま使う: 既定の外形はカード配車・地図が
 * 決める値で、そちらは 1 行に案件カードを横並びに置くだけなので高さが要らない。
 * 長距離だけ 116px にするので、カード高は器のぶんを引いて逆算する（下記）。
 */
export const LONG_HAUL_ROW_HEIGHT = 116;

/**
 * 左カラムの車両カードの高さ。行の外形（116px）から器のパディングと境界線を引いた残り。
 *
 * カード側を先に決めないのは、揃えたいのが「左の 1 行の外形」と「中央のレーン」だから。
 * 外形を先に置いて中を合わせれば、器のパディングを動かしても縦ずれが出ない。
 */
export const LONG_HAUL_VEHICLE_CARD_HEIGHT =
  cardHeightForSingleRowGroup(LONG_HAUL_ROW_HEIGHT);

/** 日付 1 列の幅（4 日以上を並べるとき） */
export const LONG_HAUL_DAY_COL_WIDTH = 152;

/** 日付 1 列の幅（3 日以下のとき） */
export const LONG_HAUL_WIDE_DAY_COL_WIDTH = 260;

/** 広い列を使う上限の日数 */
export const LONG_HAUL_WIDE_DAY_COL_MAX_DAYS = 3;

/**
 * 表示日数から日列の幅を決める。サンプル（route-planner
 * `components/dispatch/MultiDayBoard.tsx:393-394`）と同じ 3 日以下 = 260 / 4 日以上 = 152。
 *
 * 広げるのは、少ない日数のままだと盤面が器の左端に寄って右側が丸ごと余白になるため。
 * 列が広がるとバーも広がり、日を共有して 2 分割されても文字が出せる
 * （260 / 2 = 130px ≧ `LongHaulBoard` の `BAR_TEXT_MIN_WIDTH` 110px。
 * 152 / 2 = 76px では出ない）。この関係は LongHaulBoard.test.tsx が固定している。
 *
 * Why not 器の幅に合わせて可変にする: 中央ボードはスクロールを持たず日列の合計幅なりに
 * 伸びる作りで、器の幅は測っていない。測って割り付けると、右カラムの幅を調整するたびに
 * （#338 C3）列幅とバーの位置まで動く盤面になる。
 */
export function longHaulDayColWidth(dayCount: number): number {
  return dayCount > LONG_HAUL_WIDE_DAY_COL_MAX_DAYS
    ? LONG_HAUL_DAY_COL_WIDTH
    : LONG_HAUL_WIDE_DAY_COL_WIDTH;
}

/**
 * 日付ヘッダ 2 段（上段 = 曜日 / 下段 = 日付の角丸枠）の寸法。
 *
 * 数値を定数で持つのは、3 つの合計が `LONG_HAUL_HEADER_HEIGHT`（= 左カラムのヘッダ高 73px）
 * を超えないことをテストで固定するため。超えると 2 段目がヘッダからはみ出し、左カラムと
 * 高さを揃えている前提が視覚的に崩れる。
 *
 * Why not ヘッダ高そのものを広げる: `LONG_HAUL_HEADER_HEIGHT` は左カラム
 * （`ConvenienceDispatchVehicleList` のヘッダ 2 段）が決める値で、カード型配車も同じ定数を
 * 使う。73px は 2 段（16 + 4 + 26 = 46px）を収めるのに足りるので広げる必要が無く、
 * 広げれば既存 2 画面のヘッダまで一緒に動く。
 */
export const LONG_HAUL_WEEKDAY_ROW_HEIGHT = 16;
export const LONG_HAUL_DAY_HEADER_GAP = 4;
export const LONG_HAUL_DAY_BOX_HEIGHT = 26;
